import { ReactElement } from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AutoGraphRoundedIcon from "@mui/icons-material/AutoGraphRounded";
import BalanceRoundedIcon from "@mui/icons-material/BalanceRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import CloudSyncRoundedIcon from "@mui/icons-material/CloudSyncRounded";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import ShoppingCartCheckoutRoundedIcon from "@mui/icons-material/ShoppingCartCheckoutRounded";
import { useSetAtom } from "jotai";

import { dic } from "@common/text_defs";
import { handleOpenDlgContactUs } from "@pages/contact/ContactUsDialog";

const capabilities = [
  {
    title: "AI需要予測",
    description: "気象・実績データを活用し、日々の需要計画を支える予測値を作成します。",
    icon: AutoGraphRoundedIcon,
  },
  {
    title: "同時同量監視",
    description: "計画値と実績値の差分を可視化し、需給バランスを継続的に把握します。",
    icon: BalanceRoundedIcon,
  },
  {
    title: "ポジション管理",
    description: "小売・発電・FIT特例1を一元管理し、調達量と販売量を俯瞰できます。",
    icon: InsightsRoundedIcon,
  },
  {
    title: "計画作成・提出",
    description: "予測と調達状況をもとに計画を作成し、提出業務までスムーズにつなげます。",
    icon: SendRoundedIcon,
  },
  {
    title: "市場入札",
    description: "JEPXスポット市場と時間前市場への入札業務を一つの流れで管理します。",
    icon: ShoppingCartCheckoutRoundedIcon,
  },
];

const flowSteps = [
  { label: "予測", detail: "需要・発電", icon: AutoGraphRoundedIcon },
  { label: "計画", detail: "需給計画", icon: HubRoundedIcon },
  { label: "調達", detail: "市場入札", icon: ShoppingCartCheckoutRoundedIcon },
  { label: "監視", detail: "同時同量", icon: CloudSyncRoundedIcon },
];

const WorkGridSyncPage = (): ReactElement => {
  const openContactUsDlg = useSetAtom(handleOpenDlgContactUs);

  return (
    <Box component="main" sx={{ bgcolor: "#f7f9f8", color: "#17322c" }}>
      <Box
        component="section"
        sx={{
          position: "relative",
          overflow: "hidden",
          pt: { xs: 15, md: 18 },
          pb: { xs: 8, md: 11 },
          bgcolor: "#0d3b35",
          color: "#fff",
          backgroundImage:
            "linear-gradient(135deg, rgba(12, 59, 53, 0.98) 0%, rgba(13, 59, 53, 0.92) 55%, rgba(13, 97, 79, 0.88) 100%), repeating-linear-gradient(90deg, transparent 0, transparent 79px, rgba(255,255,255,0.06) 80px)",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
            <Grid xs={12} md={7}>
              <Typography component="p" sx={{ color: "#8de0bd !important", fontSize: 14, fontWeight: 700, mb: 2 }}>
                ENERGY BALANCING PLATFORM
              </Typography>
              <Typography
                component="h1"
                sx={{ fontSize: { xs: 38, sm: 48, md: 58 }, fontWeight: 800, lineHeight: 1.25, mb: 3 }}
              >
                {dic.works.grid_sync.title}
              </Typography>
              <Typography sx={{ maxWidth: 680, fontSize: { xs: 16, md: 18 }, lineHeight: 1.9, color: "#e0eeea !important" }}>
                {dic.works.grid_sync.desc}
              </Typography>
              <Button
                variant="contained"
                endIcon={<ArrowForwardRoundedIcon />}
                onClick={() => openContactUsDlg(() => undefined)}
                sx={{
                  mt: 4,
                  px: 3,
                  py: 1.4,
                  bgcolor: "#f2b84b",
                  color: "#17322c",
                  fontWeight: 700,
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#ffd06e", boxShadow: "none" },
                }}
              >
                導入について相談する
              </Button>
            </Grid>
            <Grid xs={12} md={5}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, sm: 4 },
                  borderRadius: 2,
                  bgcolor: "rgba(255, 255, 255, 0.08)",
                  border: "1px solid rgba(255, 255, 255, 0.18)",
                  color: "#fff",
                  backdropFilter: "blur(8px)",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                  <BoltRoundedIcon sx={{ color: "#f2b84b" }} />
                  <Typography sx={{ color: "#fff !important", fontSize: 18, fontWeight: 700 }}>需給業務を一つの流れに</Typography>
                </Stack>
                <Stack spacing={1.5}>
                  {flowSteps.map((step, index) => {
                    const StepIcon = step.icon;
                    return (
                      <Stack key={step.label} direction="row" alignItems="center" spacing={2}>
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            flexShrink: 0,
                            display: "grid",
                            placeItems: "center",
                            borderRadius: "50%",
                            bgcolor: index === flowSteps.length - 1 ? "#f2b84b" : "#2b7a68",
                            color: index === flowSteps.length - 1 ? "#17322c" : "#fff",
                          }}
                        >
                          <StepIcon fontSize="small" />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ color: "#fff !important", fontWeight: 700 }}>{step.label}</Typography>
                          <Typography sx={{ color: "#bcd4ce !important", fontSize: 13 }}>{step.detail}</Typography>
                        </Box>
                        {index < flowSteps.length - 1 && (
                          <ArrowForwardRoundedIcon sx={{ ml: "auto !important", color: "#8de0bd" }} />
                        )}
                      </Stack>
                    );
                  })}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container component="section" maxWidth="lg" sx={{ py: { xs: 8, md: 11 } }}>
        <Box sx={{ maxWidth: 720, mb: 6 }}>
          <Chip label="FULL PACKAGE" size="small" sx={{ mb: 2, bgcolor: "#dcefe8", color: "#175c4d", fontWeight: 700 }} />
          <Typography component="h2" sx={{ fontSize: { xs: 30, md: 40 }, fontWeight: 800, mb: 2 }}>
            需給管理に必要な機能を集約
          </Typography>
          <Typography sx={{ color: "#566b66", fontSize: 16, lineHeight: 1.9 }}>
            分散しがちな予測、ポジション、計画、市場取引の情報を一元化。担当者が状況を素早く判断し、日々の需給業務を確実に進められる環境を提供します。
          </Typography>
        </Box>
        <Grid container spacing={3}>
          {capabilities.map((capability, index) => {
            const CapabilityIcon = capability.icon;
            return (
              <Grid key={capability.title} xs={12} sm={6} md={index < 3 ? 4 : 6}>
                <Paper
                  elevation={0}
                  sx={{ height: "100%", p: 3.5, borderRadius: 2, border: "1px solid #dbe4e1", bgcolor: "#fff" }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      display: "grid",
                      placeItems: "center",
                      borderRadius: 1.5,
                      bgcolor: index === 4 ? "#fff0d2" : "#dcefe8",
                      color: index === 4 ? "#9b6211" : "#17624f",
                      mb: 2.5,
                    }}
                  >
                    <CapabilityIcon />
                  </Box>
                  <Typography component="h3" sx={{ fontSize: 20, fontWeight: 700, mb: 1.5 }}>
                    {capability.title}
                  </Typography>
                  <Typography sx={{ color: "#63746f", lineHeight: 1.8 }}>{capability.description}</Typography>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Container>

      <Box component="section" sx={{ bgcolor: "#e8efec", py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">
            <Grid xs={12} md={5}>
              <Typography component="h2" sx={{ fontSize: { xs: 30, md: 38 }, fontWeight: 800, lineHeight: 1.4, mb: 2 }}>
                判断の速さと、運用の確かさを両立
              </Typography>
              <Typography sx={{ color: "#566b66", fontSize: 16, lineHeight: 1.9 }}>
                需給に関わる情報を同じ基盤で扱うことで、確認や転記の負担を抑え、変化する市場と実績に応じた意思決定を支援します。
              </Typography>
            </Grid>
            <Grid xs={12} md={7}>
              <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, borderRadius: 2, bgcolor: "#fff" }}>
                <Stack spacing={3} divider={<Divider flexItem />}>
                  {[
                    ["01", "情報を一元化", "予測・計画・実績・取引を横断して把握"],
                    ["02", "変化を見逃さない", "計画値と実績値の差分を継続的に監視"],
                    ["03", "日々の業務を効率化", "計画提出から市場入札までを一つの流れで管理"],
                  ].map(([number, title, detail]) => (
                    <Stack key={number} direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1, sm: 3 }}>
                      <Typography sx={{ color: "#2b7a68", fontSize: 14, fontWeight: 800, pt: 0.5 }}>{number}</Typography>
                      <Box>
                        <Typography sx={{ fontSize: 18, fontWeight: 700, mb: 0.5 }}>{title}</Typography>
                        <Typography sx={{ color: "#63746f" }}>{detail}</Typography>
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ bgcolor: "#17322c", color: "#fff", py: { xs: 7, md: 9 } }}>
        <Container maxWidth="md" sx={{ textAlign: "center" }}>
          <Typography component="h2" sx={{ fontSize: { xs: 28, md: 36 }, fontWeight: 800, mb: 2 }}>
            需給管理業務に合わせてご提案します
          </Typography>
          <Typography sx={{ color: "#c5d9d3 !important", lineHeight: 1.8, mb: 4 }}>
            導入のご相談や機能についてのご質問は、お問い合わせフォームよりご連絡ください。
          </Typography>
          <Button
            variant="contained"
            endIcon={<ArrowForwardRoundedIcon />}
            onClick={() => openContactUsDlg(() => undefined)}
            sx={{
              px: 3.5,
              py: 1.4,
              bgcolor: "#f2b84b",
              color: "#17322c",
              fontWeight: 700,
              boxShadow: "none",
              "&:hover": { bgcolor: "#ffd06e", boxShadow: "none" },
            }}
          >
            お問い合わせ
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default WorkGridSyncPage;
